import styled, { keyframes, css } from 'styled-components';

// Keyframes
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Page wrapper
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom right, #000, #0f172a, #000);
  color: #fff;
  padding: 32px;
`;

export const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  position: relative;
`;

// Header
export const Header = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

export const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
`;

export const HeaderSubtitle = styled.p`
  font-size: 14px;
  opacity: 0.7;
`;

export const CurrentPlanBadge = styled.button`
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 14px;
  border: 1px solid;
  transition: all 0.2s;
  cursor: pointer;
  background: ${props => props.$isPro 
    ? 'linear-gradient(to right, rgba(6, 182, 212, 0.3), rgba(14, 165, 233, 0.3))' 
    : 'rgba(255, 255, 255, 0.05)'};
  border-color: ${props => props.$isPro ? 'rgba(6, 182, 212, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  color: #fff;

  &:hover {
    background: ${props => props.$isPro 
      ? 'linear-gradient(to right, rgba(6, 182, 212, 0.5), rgba(14, 165, 233, 0.5))' 
      : 'rgba(255, 255, 255, 0.1)'};
    box-shadow: ${props => props.$isPro ? '0 0 20px rgba(0, 247, 210, 0.45)' : 'none'};
  }
`;

// Period selector
export const PeriodSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

export const PeriodButton = styled.button`
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 14px;
  border: 1px solid;
  transition: all 0.2s;
  cursor: pointer;
  background: ${props => props.$active 
    ? 'linear-gradient(to right, rgba(6, 182, 212, 0.3), rgba(14, 165, 233, 0.3))' 
    : 'rgba(255, 255, 255, 0.05)'};
  border-color: ${props => props.$active ? 'rgba(6, 182, 212, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  box-shadow: ${props => props.$active ? '0 0 15px rgba(0, 247, 210, 0.35)' : 'none'};
  color: #fff;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

// Sidebar overlay
export const SidebarOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
`;

export const SidebarBackdrop = styled.div`
  flex: 1;
  background: rgba(0, 0, 0, 0.6);
`;

export const Sidebar = styled.div`
  width: 380px;
  background: #0f172a;
  border-left: 1px solid rgba(6, 182, 212, 0.2);
  padding: 24px;
  padding-top: 80px;
  overflow-y: auto;
  height: 100vh;
`;

export const SidebarTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 16px;
`;

export const SidebarInfo = styled.div`
  font-size: 14px;
  margin-bottom: 24px;
`;

export const SidebarInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  span:first-child {
    opacity: 0.7;
  }

  span.highlight {
    color: #67e8f9;
  }
`;

export const SidebarActions = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

export const SidebarActionBtn = styled.button`
  flex: 1;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 14px;
  border: 1px solid;
  transition: all 0.2s;
  cursor: pointer;
  background: ${props => props.$primary 
    ? 'linear-gradient(to right, rgba(6, 182, 212, 0.3), rgba(14, 165, 233, 0.3))' 
    : 'rgba(255, 255, 255, 0.05)'};
  border-color: ${props => props.$primary ? 'rgba(6, 182, 212, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  color: #fff;
  opacity: ${props => props.disabled ? 0.4 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  &:hover:not(:disabled) {
    background: ${props => props.$primary 
      ? 'linear-gradient(to right, rgba(6, 182, 212, 0.5), rgba(14, 165, 233, 0.5))' 
      : 'rgba(255, 255, 255, 0.1)'};
    box-shadow: ${props => props.$primary ? '0 0 20px rgba(0, 247, 210, 0.45)' : 'none'};
  }
`;

export const PlanOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

export const PlanOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  border: 1px solid rgba(6, 182, 212, 0.2);
  padding: 8px;
`;

export const PlanOptionInfo = styled.div`
  p:first-child {
    font-size: 14px;
    font-weight: 500;
  }
  p:last-child {
    font-size: 12px;
    opacity: 0.7;
  }
`;

export const PlanOptionBtn = styled.button`
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  background: linear-gradient(to right, rgba(6, 182, 212, 0.3), rgba(14, 165, 233, 0.3));
  border: 1px solid rgba(6, 182, 212, 0.4);
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(to right, rgba(6, 182, 212, 0.5), rgba(14, 165, 233, 0.5));
  }
`;

// Renewal progress
export const RenewalProgress = styled.div`
  margin-bottom: 24px;
`;

export const RenewalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 4px;

  span:first-child {
    opacity: 0.7;
  }
`;

export const RenewalBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 9999px;
  overflow: hidden;
`;

export const RenewalFill = styled.div`
  height: 8px;
  background: linear-gradient(to right, #06b6d4, #0ea5e9);
  transition: width 0.3s;
  width: ${props => props.$progress}%;
`;

export const ViewPlansBtn = styled.button`
  margin-top: 16px;
  width: 100%;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.15);
  }
`;

// Plans grid
export const PlansSection = styled.div`
  margin-bottom: 64px;
`;

export const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const PlanCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(6, 182, 212, 0.2);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s;

  &:hover {
    border-color: rgba(6, 182, 212, 0.5);
    box-shadow: 0 0 35px rgba(0, 247, 210, 0.25);
    transform: translateY(-4px);
  }
`;

export const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

export const PlanName = styled.h3`
  font-size: 18px;
  font-weight: 500;
`;

export const BestValueBadge = styled.span`
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  border-radius: 9999px;
  background: linear-gradient(to right, #facc15, #f59e0b);
  color: #000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

export const PlanPricing = styled.div`
  margin-bottom: 16px;
`;

export const PlanPrice = styled.p`
  font-size: 30px;
  font-weight: 600;
`;

export const PlanPeriodLabel = styled.p`
  font-size: 12px;
  opacity: 0.6;
`;

export const PlanSaving = styled.p`
  font-size: 12px;
  color: #86efac;
  margin-top: 4px;
  transition: all 0.5s;
  ${props => props.$animate && css`
    animation: ${pulse} 0.6s ease-in-out;
    transform: scale(1.05);
  `}
`;

export const ChoosePlanBtn = styled.button`
  margin-top: auto;
  padding: 8px 16px;
  border-radius: 12px;
  background: linear-gradient(to right, rgba(6, 182, 212, 0.3), rgba(14, 165, 233, 0.3));
  border: 1px solid rgba(6, 182, 212, 0.4);
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(to right, rgba(6, 182, 212, 0.5), rgba(14, 165, 233, 0.5));
    box-shadow: 0 0 25px rgba(0, 247, 210, 0.45);
    transform: scale(1.03);
  }

  &:active {
    transform: scale(0.98);
  }
`;

// Feature matrix
export const FeatureMatrixSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

export const FeatureMatrixCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(6, 182, 212, 0.2);
  border-radius: 16px;
  padding: 24px;
`;

export const FeatureMatrixTitle = styled.h3`
  font-weight: 500;
  margin-bottom: 16px;
`;

export const FeatureTable = styled.table`
  width: 100%;
  font-size: 14px;
  border-collapse: collapse;
  table-layout: fixed;
`;

export const FeatureTableHead = styled.thead`
  tr {
    text-align: left;
  }

  th {
    padding: 12px 16px;
    transition: color 0.2s;
    font-weight: 600;

    &:first-child {
      width: 40%;
      text-align: left;
    }

    &:not(:first-child) {
      text-align: center;
    }

    &:hover {
      color: #67e8f9;
    }
  }
`;

export const FeatureTableBody = styled.tbody`
  tr {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  td {
    padding: 12px 16px;

    &:first-child {
      opacity: 0.7;
      text-align: left;
    }

    &:not(:first-child) {
      text-align: center;
    }
  }
`;

export const FeatureCheck = styled.span`
  color: #4ade80;
`;

export const FeatureX = styled.span`
  color: #f87171;
`;

export const FeatureText = styled.span`
  font-size: 12px;
`;
